import type { SceneObject } from "../SceneHost";
import { objectRotationRad } from "../transform/rotate";

/**
 * Обычный размер виджетов (как при создании, см. lib/board/create*Object).
 * Растянули больше — содержимое крупнее, сжали — мельче.
 */
const WIDGET_BASE_SIZE: Record<string, { w: number; h: number }> = {
  timer: { w: 300, h: 248 },
  code: { w: 580, h: 500 },
  sql: { w: 780, h: 580 },
  calculator: { w: 420, h: 640 },
};
const GRAPH_BASE_SIZE = { w: 640, h: 560 };
const CONTENT_SCALE_MIN = 0.5;
const CONTENT_SCALE_MAX = 3;

/**
 * Во сколько раз увеличить содержимое виджета. Берём меньшую из сторон:
 * растянули только вширь — места просто больше, растянули за угол —
 * буквы, цифры и кнопки растут вместе с рамкой.
 */
function widgetBaseSize(obj: SceneObject): { w: number; h: number } | null {
  const base = WIDGET_BASE_SIZE[obj.type];
  if (!base) return null;
  if (obj.type === "calculator") {
    const modes = (obj as { enabledModes?: unknown }).enabledModes;
    if (Array.isArray(modes) && modes.includes("graph")) return GRAPH_BASE_SIZE;
  }
  return base;
}

export function widgetContentScale(obj: SceneObject, w: number, h: number): number {
  const base = widgetBaseSize(obj);
  return base ? scaleFor(base, w, h) : 1;
}

function scaleFor(base: { w: number; h: number }, w: number, h: number): number {
  const s = Math.min(w / base.w, h / base.h);
  if (!Number.isFinite(s) || s <= 0) return 1;
  // Около обычного размера — без масштаба: виджет выглядит ровно как раньше.
  if (Math.abs(s - 1) < 0.04) return 1;
  return Math.min(CONTENT_SCALE_MAX, Math.max(CONTENT_SCALE_MIN, s));
}

/**
 * Размещение объекта, который рисуется обычным HTML рядом со слоем SVG.
 *
 * Safari (а на iPhone и iPad движок один у всех браузеров) не применяет к HTML
 * внутри SVG преобразования сцены: объект рисуется в левом верхнем углу
 * видимой части доски, а не на своём месте. Поэтому такие объекты живут
 * в отдельном слое, а место им задаётся обычным CSS — слой камеры уже сдвинут
 * и масштабирован, так что единица здесь равна единице сцены.
 */
export function placeHtmlObject(
  el: HTMLElement,
  obj: SceneObject,
  w: number,
  h: number
): void {
  el.style.position = "absolute";
  // Без округления: при перетаскивании объект двигают дробными координатами,
  // и округление здесь рассогласовывало объект с рамкой выделения — на каждом
  // пересчёте сцены картинка дёргалась назад на пиксель.
  el.style.left = `${Number(obj.x) || 0}px`;
  el.style.top = `${Number(obj.y) || 0}px`;
  el.style.width = `${w}px`;
  el.style.height = `${h}px`;
  // У объектов бывают части, выходящие за рамку (полоска слайдов справа),
  // а у `.wb-scene-obj` в стилях стоит overflow: hidden — без этого объект
  // обрезался бы по своей рамке, а при нулевом размере пропадал целиком.
  el.style.overflow = "visible";

  // Обычный размер виджета — на сам элемент: по нему масштаб пересчитывается
  // и во время растягивания, когда объекта под рукой нет (applyObjectBounds).
  const base = widgetBaseSize(obj);
  if (base) {
    el.dataset.contentBaseW = String(base.w);
    el.dataset.contentBaseH = String(base.h);
  }
  applyWidgetContentScale(el, w, h);

  const rot = objectRotationRad(obj);
  if (Math.abs(rot) < 1e-6) {
    el.style.transform = "";
    el.style.transformOrigin = "";
    return;
  }
  // В SVG поворот шёл вокруг центра объекта — в CSS повторяем то же.
  el.style.transformOrigin = "50% 50%";
  el.style.transform = `rotate(${(rot * 180) / Math.PI}deg)`;
}

/**
 * Масштаб содержимого виджета — через переменную: карточку внутри
 * растягивает и масштабирует CSS (board-editor.css, [data-content-scale]).
 */
export function applyWidgetContentScale(el: HTMLElement, w: number, h: number): void {
  const bw = Number(el.dataset.contentBaseW);
  const bh = Number(el.dataset.contentBaseH);
  const scale = bw > 0 && bh > 0 ? scaleFor({ w: bw, h: bh }, w, h) : 1;
  if (scale === 1) {
    if (el.dataset.contentScale !== undefined) delete el.dataset.contentScale;
    el.style.removeProperty("--wb-content-scale");
    return;
  }
  el.dataset.contentScale = "";
  el.style.setProperty("--wb-content-scale", String(Math.round(scale * 1000) / 1000));
}
